import { useEffect, useMemo, useState } from "react";
import { LANG_LABELS, LANG_SHORT, aiJobOpen, allergenName, businessPhrases, editsCatalog, editsMenus, proofreadOn, hasWords, isDefaultAllergen, isDietTag, tagIn, menuView, phraseKey, phraseOf, PHRASE_LIMITS, textStatus, viewPhrases } from "@speisekarte/core";
import type { AiJob, Allergen, Lang, MenuInput, TargetCounts, TextSource, TextStatus, TextTarget } from "@speisekarte/core";
import { useAiJob } from "../ai-job.ts";
import { ApiError, api, dishesById, messageOf, store, textsById } from "../store.ts";
import type { Data } from "../store.ts";
import { useWorkLang } from "./LangChoice.tsx";
import { noteModel, useModel } from "../model.ts";

/**
 * „Texte prüfen und übersetzen“ (S61): what the review page, step 2 of a card, the lists and the forms
 * share – one count of every text's state for every target („Deutsch ✓“ and each language), the jobs
 * of the model followed until done, the line of a translation under a German field.
 */

/** Every text of the business the dictionary translates (S36), with where it first comes from. */
export const phrasesOfData = (data: Data) =>
  businessPhrases({ dishes: data.dishes, allergens: data.allergens, texts: data.texts, categories: data.categories, drinkCategories: data.drinkCategories, tags: data.tags, drinkTags: data.drinkTags, menus: data.menus, tagIcons: data.tagIcons, footer: data.settings.footer });

/** What the status counts; „Deutsch ✓“ only with a model to check it (S61 § 11). */
export const textSourceOf = (data: Data, model = true): TextSource => ({
  dishes: data.dishes,
  allergens: data.allergens,
  texts: data.texts,
  categories: data.categories,
  drinkCategories: data.drinkCategories,
  tags: data.tags,
  drinkTags: data.drinkTags,
  menus: data.menus,
  tagIcons: data.tagIcons,
  footer: data.settings.footer,
  languages: data.settings.languages ?? [],
  proofread: proofreadOn(data.settings) && model,
  proofs: data.proofs ?? {},
  phrases: data.phrases ?? {},
});

/** The status of the catalogue, or of one card (a draft too, with its id). */
export function useTextStatus(data: Data, menu: (MenuInput & { id: string }) | null = null): TextStatus {
  const model = useModel();
  return useMemo(() => textStatus(textSourceOf(data, model), menu ? { menu } : null), [data, menu, model]);
}

/** Whether anything of it shows (S38): a language or „Deutsch prüfen“ switched on. */
export const textsOn = (data: Data) => proofreadOn(data.settings);

/**
 * What it is called, wherever it is offered (S61 § 11, Martin 02.11.2026): „Texte prüfen“ with the
 * German check alone, „Texte prüfen und übersetzen“ with languages, „Texte übersetzen“ without a model
 * (by hand only).
 */
export const textsName = (data: Data, model: boolean) => ((data.settings.languages ?? []).length ? (model ? "Texte prüfen und übersetzen" : "Texte übersetzen") : "Texte prüfen");

/**
 * What a button into the flow is called by what is still to do (Martin, 02.11.2026): the German all
 * checked, „Texte übersetzen“; the languages all done, „Texte prüfen“; else, or with nothing left,
 * the name as offered (`textsName`). The page keeps its fixed title.
 */
export function textsAction(data: Data, model: boolean, status: TextStatus): string {
  // Nothing due: the way to look at them and change one by hand, named by its goal (UI concept § 6a, Martin 02.11.2026).
  if (allDone(status)) return "Texte ansehen";
  const name = textsName(data, model);
  if (name !== "Texte prüfen und übersetzen") return name;
  const de = status.counts.de;
  const german = de ? de.open.dishes + de.open.texts + de.review.dishes + de.review.texts > 0 : false;
  const langs = status.targets.some((t) => t !== "de" && toReview(status.counts[t]) + (status.counts[t]?.open.dishes ?? 0) + (status.counts[t]?.open.texts ?? 0) > 0);
  return german && !langs ? "Texte prüfen" : langs && !german ? "Texte übersetzen" : name;
}

/** „1 Text“, „60 Texte“: every entry is one, a dish with its name and description too (S61 § 11). */
export const textsCount = (n: number) => (n === 1 ? "1 Text" : `${n} Texte`);

export const targetLabel = (t: TextTarget) => (t === "de" ? "Deutsch ✓" : LANG_LABELS[t]);

/** The review page, of the catalogue or a card, maybe in a language, maybe to finish it („Fertig machen“). */
export function reviewHref(menu: string | null, options: { lang?: Lang; flow?: boolean; filter?: "pruefen" } = {}): string {
  const q = new URLSearchParams();
  if (options.lang) q.set("sprache", options.lang);
  if (options.flow) q.set("ablauf", "1");
  if (options.filter) q.set("filter", options.filter);
  const query = q.toString();
  return `#/texte-pruefen${menu ? `/${menu}` : ""}${query ? `?${query}` : ""}`;
}

/** How many to look through: corrections open, the model's translations, and those out of date. */
export const toReview = (c: TargetCounts | undefined) => (c ? c.review.dishes + c.review.texts + c.stale.dishes + c.stale.texts : 0);

/** One target's state in words (S61 § 11): „geprüft ✓“, „60 Texte nicht übersetzt · 3 Texte veraltet“. */
export function targetText(t: TextTarget, c: TargetCounts | undefined): { text: string; done: boolean } {
  if (!c) return { text: "", done: true };
  const parts: string[] = [];
  const open = c.open.dishes + c.open.texts;
  const review = c.review.dishes + c.review.texts;
  const stale = c.stale.dishes + c.stale.texts;
  if (t === "de") {
    if (open) parts.push(`${textsCount(open)} ungeprüft`);
    if (review) parts.push(`Vorschläge offen in ${review === 1 ? "1 Text" : `${review} Texten`}`);
    return { text: parts.join(" · ") || "geprüft ✓", done: !parts.length };
  }
  if (open) parts.push(`${textsCount(open)} nicht übersetzt`);
  if (review) parts.push(`${textsCount(review)} nicht durchgesehen`);
  if (stale) parts.push(`${textsCount(stale)} veraltet`);
  return { text: parts.join(" · ") || "vollständig ✓", done: !parts.length };
}

/** A line per target: „Deutsch ✓ · geprüft ✓“, „Spanisch · 60 Texte nicht übersetzt“. */
export function TargetRows({ status, testid = "text-targets" }: { status: TextStatus; testid?: string }) {
  if (!status.targets.length) return null;
  return (
    <ul className="target-rows" data-testid={testid}>
      {status.targets.map((t) => {
        const { text, done } = targetText(t, status.counts[t]);
        return (
          <li key={t} data-testid={`${testid}-${t}`}>
            <span className="target-name">{targetLabel(t)}</span>
            <span className={done ? "ok-text" : ""}>{text}</span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

type Jobs = { available: boolean; translate: AiJob<"translate"> | null; proofread: AiJob<"proofread"> | null };

/** What the model left out even when asked again (03.11.2026): it stays open, the next tap asks for it. */
const leftText = (left: number | undefined, done: string) => (left ? `${left === 1 ? "1 Text blieb" : `${left} Texte blieben`} un${done}; bitte noch einmal starten.` : "");

/**
 * The jobs of the model for texts (S45, S61), followed while the page is open; what came out stays as
 * a line until the next job (never only a toast, S61: „Bitte durchsehen“ said where).
 */
export function useTextJobs(version: number) {
  const [state, setState] = useState<Jobs | null>(null);
  const [translate, setTranslate] = useState<AiJob<"translate"> | null>(null);
  const [proofread, setProofread] = useState<AiJob<"proofread"> | null>(null);
  // `next` what to do now: said only while there is something to look through (not when all is done, 02.11.2026).
  const [result, setResult] = useState<{ text: string; next?: string; error?: boolean } | null>(null);
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    let live = true;
    api<Jobs>("/api/texts")
      .then((s) => {
        noteModel(s.available);
        if (!live) return;
        setState(s);
        if (s.translate) setTranslate(s.translate);
        if (s.proofread) setProofread(s.proofread);
      })
      .catch(() => live && setState(null));
    return () => {
      live = false;
    };
  }, [version]);
  useAiJob(translate, setTranslate, (done) => {
    if (done.status === "done" && done.result) {
      const r = done.result;
      const by = (r.langs ?? [r.lang]).map((l) => `${r.byLang?.[l] ?? r.translated} auf ${LANG_LABELS[l]}`);
      const next = [r.translated ? "Was vom Übersetzer kommt, steht unten zum Durchsehen." : "", leftText(r.left, "übersetzt")].filter(Boolean).join(" ");
      setResult(r.translated ? { text: `Übersetzt: ${by.join(", ")}.`, next } : r.left ? { text: leftText(r.left, "übersetzt") } : { text: "Nichts mehr zu übersetzen." });
    } else setResult({ text: done.error?.message ?? "Das Übersetzen ist abgebrochen. Bitte noch einmal.", error: true });
    void store.refresh();
  });
  useAiJob(proofread, setProofread, (done) => {
    if (done.status === "done" && done.result) {
      const { suggestions, left } = done.result;
      const rest = leftText(left, "geprüft");
      setResult(suggestions === 0 ? { text: "Deutsch geprüft, nichts gefunden.", ...(rest ? { next: rest } : {}) } : { text: `Deutsch geprüft: ${suggestions === 1 ? "1 Vorschlag" : `${suggestions} Vorschläge`}.`, next: ["Sie stehen unten zum Durchsehen; nichts ist geändert.", rest].filter(Boolean).join(" ") });
    } else setResult({ text: done.error?.message ?? "Das Prüfen ist abgebrochen. Bitte noch einmal.", error: true });
    void store.refresh();
  });
  async function start(kind: "translate" | "proofread", body: object) {
    setResult(null);
    setStarting(true);
    try {
      const job = (await api<{ job: AiJob }>(kind === "translate" ? "/api/translate" : "/api/proofread", { body })).job;
      if (kind === "translate") setTranslate(job as AiJob<"translate">);
      else setProofread(job as AiJob<"proofread">);
    } catch (err) {
      setResult({ text: messageOf(err), error: !(err instanceof ApiError && err.code === "nothing") });
    } finally {
      setStarting(false);
    }
  }
  return {
    available: state?.available ?? false,
    translating: (translate !== null && aiJobOpen(translate)) || starting,
    proofing: (proofread !== null && aiJobOpen(proofread)) || starting,
    ahead: Math.max(translate?.ahead ?? 0, proofread?.ahead ?? 0),
    result,
    clear: () => setResult(null),
    translate: (langs: readonly Lang[], menu: string | null) => start("translate", { langs, ...(menu ? { menu } : {}) }),
    proofread: (menu: string | null) => start("proofread", menu ? { menu } : {}),
  };
}
export type TextJobs = ReturnType<typeof useTextJobs>;

/** What a job is doing, or what came out of the last one. */
export function JobLine({ jobs, done = false }: { jobs: TextJobs; done?: boolean }) {
  const busy = jobs.translating ? "Wird übersetzt …" : jobs.proofing ? "Wird geprüft …" : "";
  if (busy) {
    return (
      <p className="notice job-line" data-testid="job-busy">
        {busy} {jobs.ahead > 0 ? `Noch ${jobs.ahead === 1 ? "ein Auftrag" : `${jobs.ahead} Aufträge`} vor dir. ` : ""}Das dauert etwa eine Minute; die Seite darf verlassen werden.
      </p>
    );
  }
  if (!jobs.result) return null;
  return (
    <p className={`${jobs.result.error ? "error-box" : "notice"} job-line`} data-testid="job-result">
      {jobs.result.text}
      {jobs.result.next && !done ? ` ${jobs.result.next}` : ""}{" "}
      <button className="link inline" onClick={jobs.clear} aria-label="Meldung schließen">
        ✕
      </button>
    </p>
  );
}

/** „etwa 12 ct“ from dollars; „unter 1 ct“ for less. */
export const centsText = (dollars: number | null) => (dollars === null ? "" : dollars < 0.01 ? "unter 1 ct" : `etwa ${Math.round(dollars * 100)} ct`);

// ---------------------------------------------------------------------------
// The translation under a German field (S61 § 4.1)
// ---------------------------------------------------------------------------

/** How many cards show each text of the dictionary, kept per state of the data. */
const usedBy = new WeakMap<Data, Map<string, number>>();
function cardsUsing(data: Data): Map<string, number> {
  let m = usedBy.get(data);
  if (m) return m;
  m = new Map();
  const dishes = dishesById(data);
  const texts = textsById(data);
  for (const menu of data.menus) for (const t of viewPhrases(menuView(menu, dishes, data.allergens, texts, data.tagIcons))) m.set(t, (m.get(t) ?? 0) + 1);
  usedBy.set(data, m);
  return m;
}

/** The state of a text's translation as a word, and whether „Passt“ goes with it. */
function phraseState(text: string, lang: Lang, data: Data): { word: string; ai: boolean; missing: boolean; base: boolean } {
  const found = phraseOf(text, lang, data.phrases ?? {});
  if (!found) return { word: "fehlt", ai: false, missing: true, base: false };
  if (found.by === "ai") return { word: "nicht durchgesehen", ai: true, missing: false, base: false };
  if (found.by === "base") return { word: "Vorgabe", ai: false, missing: false, base: true };
  return { word: "", ai: false, missing: false, base: false };
}

/** „Passt“ (S61): translations by the model looked through. */
export async function checked(lang: Lang, items: { dishes?: string[]; texts?: string[] }) {
  await store.change(() => api("/api/translations/checked", { body: { lang, dishes: items.dishes ?? [], texts: items.texts ?? [] } }));
}

/** One text's translation into one language: a field, its state, „Passt“; saved when the field is left. */
export function PhraseField({ text, lang, data, onError, long = false, title }: { text: string; lang: Lang; data: Data; onError: (m: string) => void; long?: boolean; title?: string }) {
  const canEdit = editsMenus(data.role);
  const found = phraseOf(text, lang, data.phrases ?? {});
  const st = phraseState(text, lang, data);
  async function save(value: string) {
    // The base vocabulary's left as it is stays the base: never copied, so a correction reaches it.
    if (phraseKey(value) === (found?.text ?? "")) return;
    try {
      await store.change(() => api("/api/phrases", { method: "PUT", body: { lang, phrases: { [text]: value } } }));
    } catch (err) {
      onError(messageOf(err));
    }
  }
  const field = {
    defaultValue: found?.text ?? "",
    placeholder: `${LANG_LABELS[lang]} eintragen`,
    maxLength: PHRASE_LIMITS.text,
    disabled: !canEdit,
    "aria-label": `${text} auf ${LANG_LABELS[lang]}`,
    ...(title ? { title } : {}),
    onBlur: (e: { currentTarget: { value: string } }) => void save(e.currentTarget.value),
  };
  return (
    <span className="tr-field" data-testid={`tr-${lang}`}>
      <span className="lang-code" title={LANG_LABELS[lang]}>
        {LANG_SHORT[lang]}
      </span>
      {long ? <textarea rows={2} {...field} /> : <input type="text" {...field} />}
      <span className={`tr-state ${st.missing ? "missing" : ""}`}>
        {st.word}
        {st.ai && canEdit && " "}
        {st.ai && canEdit && (
          <button type="button" className="link inline" onClick={() => void checked(lang, { texts: [text] }).catch((err) => onError(messageOf(err)))} data-testid="tr-ok">
            Passt
          </button>
        )}
      </span>
    </span>
  );
}

/**
 * Under a German field of the catalogue (S61 § 4.1, Martin: „Alle anderen Artefakte im Katalog haben
 * die Detailview nicht“): its translation into the language chosen in the catalogues, „alle Sprachen“
 * for every one. On how many cards the word stands besides only as the field's hint (Martin: the lists
 * show their counts short and grey, no sentence). Nothing without a language (S38) or for a text
 * without words („0,3 l“).
 */
export function PhraseLine({ text, data, long = false }: { text: string; data: Data; long?: boolean }) {
  const langs = data.settings.languages ?? [];
  const [work] = useWorkLang(langs);
  const [all, setAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = phraseKey(text);
  if (!work || !hasWords(key)) return null;
  // „vegan“, „scharf“ … have their own words in every language (S36): never typed, never by the model.
  if (isDietTag(key)) return <FixedLine data={data} word={(l) => tagIn(key, l)} note="fest" testid="tr-fixed" />;
  const uses = cardsUsing(data).get(key) ?? 0;
  const title = uses > 0 ? `Gilt überall, wo „${key}“ steht, auch auf ${uses === 1 ? "einer Speisekarte" : `${uses} Speisekarten`}.` : undefined;
  return (
    <div className="tr-line" data-testid="tr-line">
      {(all ? langs : [work]).map((l) => (
        <PhraseField key={`${key}|${l}|${phraseOf(key, l, data.phrases ?? {})?.text ?? ""}`} text={key} lang={l} data={data} onError={setError} long={long} {...(title ? { title } : {})} />
      ))}
      {langs.length > 1 && (
        <span className="tr-more muted small">
          <button type="button" className="link inline muted" onClick={() => setAll(!all)} data-testid="tr-all">
            {all ? `nur ${LANG_LABELS[work]}` : "alle Sprachen"}
          </button>
        </span>
      )}
      {error && <span className="error-text small">{error}</span>}
    </div>
  );
}

/**
 * A word with a translation of its own that nobody types (S36): one of the 14 allergens under the
 * regulation's name („amtlich“), a diet's tag such as „vegan“ („fest“). No field; „alle Sprachen“ as
 * under any other text.
 */
function FixedLine({ data, word, note, testid }: { data: Data; word: (l: Lang) => string; note: string; testid: string }) {
  const langs = data.settings.languages ?? [];
  const [work] = useWorkLang(langs);
  const [all, setAll] = useState(false);
  if (!work) return null;
  return (
    <div className="tr-line" data-testid={testid}>
      {(all ? langs : [work]).map((l) => (
        <span key={l} className="tr-field" data-testid={`tr-${l}`}>
          <span className="lang-code" title={LANG_LABELS[l]}>
            {LANG_SHORT[l]}
          </span>
          <span className="tr-fixed">{word(l)}</span>
          <span className="tr-state">{note}</span>
        </span>
      ))}
      {langs.length > 1 && (
        <span className="tr-more muted small">
          <button type="button" className="link inline muted" onClick={() => setAll(!all)} data-testid="tr-all">
            {all ? `nur ${LANG_LABELS[work]}` : "alle Sprachen"}
          </button>
        </span>
      )}
    </div>
  );
}

/** One of the 14 under the regulation's name (S36): its official name in each language, no field. */
export function OfficialLine({ allergen, data }: { allergen: Allergen; data: Data }) {
  if (!isDefaultAllergen(allergen)) return null;
  return <FixedLine data={data} word={(l) => allergenName(allergen, l)} note="amtlich" testid="tr-official" />;
}

// ---------------------------------------------------------------------------
// The way there: the catalogues and step 2 of a card
// ---------------------------------------------------------------------------

export const allDone = (status: TextStatus) => status.targets.every((t) => targetText(t, status.counts[t]).done);

/** Above the list of dishes (S61): where the German and the languages stand; the way there is the button above (§ 11). */
export function CatalogTexts({ data }: { data: Data }) {
  const status = useTextStatus(data);
  return (
    <div className="catalog-texts" data-testid="catalog-texts">
      <TargetRows status={status} testid="catalog-targets" />
    </div>
  );
}

/**
 * Step 2 of a card (S61 § 7, § 11): a line per target for this card, and one button into the guided
 * flow (without a model the page by hand), named as it is offered everywhere. A card changed or new is
 * saved first. Changing only how it looks changes nothing here (the button that checked again and
 * again, S61).
 */
export function CardTexts({ data, status, menu, save }: { data: Data; status: TextStatus; menu: MenuInput & { id: string }; save: (() => Promise<string | null>) | null }) {
  const [error, setError] = useState<string | null>(null);
  const model = useModel();
  if (!status.targets.length && !(data.settings.languages ?? []).length) return null;
  const done = allDone(status);
  async function go() {
    setError(null);
    try {
      const id = save ? await save() : menu.id;
      // Nothing due: the texts to look at (the expert mode), not a flow with nothing in it.
      if (id) location.hash = reviewHref(id, { flow: model && !done });
    } catch (err) {
      setError(messageOf(err));
    }
  }
  return (
    <div className="card-texts" data-testid="card-texts">
      <TargetRows status={status} testid="card-targets" />
      {editsMenus(data.role) && (
        <div className="buttons">
          <button className={done ? "link inline" : ""} onClick={() => void go()} data-testid="card-finish">
            {textsAction(data, model, status)}
          </button>
        </div>
      )}
      {save && !done && <p className="hint">Die Karte wird vorher gespeichert.</p>}
      {error && <p className="error-box">{error}</p>}
    </div>
  );
}
